import { useEffect, useRef, useState } from 'react';
import { selectTenantPrompt } from '../../copy';
import { useDelete, useOne } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router';
import { Button, Empty, Space, Tag, Typography } from 'antd';
import { DetailField, DetailPage, useConfirmAction, space } from '@viq-admin/ui';
import { Can } from '../../ability/AbilityContext';
import { useCompanyScope } from '../../components/CompanyScopePicker';
import { useUnsavedEditGuard } from '../../scope/unsavedChanges';
import { QuestionEdit } from './edit';
import { typeLabel } from './list';
import type { DiscoveryQuestion } from './types';

const { Text } = Typography;

/**
 * Discovery question detail — read-only by default, Edit flips the form in
 * place (via the shared DetailPage). One route, /questions/:id. Mirrors
 * CompanyDetail/IntegrationDetail/ToolkitDetail's structure; see
 * CompanyDetail's header comment for the 404-derivation rationale (a missing
 * record surfaces through useOne as `query.error` with `statusCode === 404`,
 * never an empty success).
 *
 * Questions Inventory is per-tenant, driven by the global "Operate as
 * tenant" scope (top-right CompanyScopePicker) — the same scope the list and
 * edit/create pages read. A fetch with no companyId 422s, so when none is
 * scoped this short-circuits to the SAME "Select a tenant…" Empty prompt the
 * list/edit/create render, WITHOUT ever calling DetailPage (no loading
 * skeleton for a query that isn't going to fire). `useOne`'s `enabled` flag
 * still gates the actual network call either way.
 *
 * The read view surfaces the FULL ordered options list (the list column only
 * shows a truncated preview) plus two flags invisible on the list today:
 * `hasOtherOption` and the free-text "no options" note. `typeLabel` is
 * imported from `list.tsx` rather than duplicated.
 *
 * Delete lives in the header (gated `manage:DiscoveryQuestion`), in addition
 * to — not instead of — the list row's kebab Delete.
 */
export function QuestionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { companyId } = useCompanyScope();
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const confirm = useConfirmAction();

  // Announce an in-flight dirty edit so the global "Operate as tenant" switch
  // (CompanyScopeProvider) confirms-before-discard instead of silently dropping
  // it — the shared DetailPage guard only covers the navigation it mediates, not
  // an external scope change that unmounts this view. See scope/unsavedChanges.
  useUnsavedEditGuard(editing && dirty);

  // The open question belongs to the tenant that was scoped when it was opened.
  // Once a scope switch commits (companyId actually changed — not the initial
  // mount), leave this record and return to the list, which reloads under the
  // new scope. Otherwise we'd keep showing another tenant's question. Dropping
  // the edit state alongside also prevents a lingering `dirty` from
  // phantom-guarding the next edit (the same failure the `onEditingChange`
  // reset prevents for in-page exits). Guarded by a ref so it fires only on a
  // real change, regardless of why the effect re-runs.
  const lastScope = useRef(companyId);
  useEffect(() => {
    if (companyId === lastScope.current) return;
    lastScope.current = companyId;
    setEditing(false);
    setDirty(false);
    navigate('/questions');
  }, [companyId, navigate]);

  const { result: question, query } = useOne<DiscoveryQuestion>({
    resource: 'questions',
    id,
    // Skip the query entirely when no tenant is scoped (avoids the 422) —
    // mirrors the list's `hasScope` gate.
    queryOptions: { enabled: id != null && companyId != null },
  });
  const { mutateAsync: deleteQuestion } = useDelete();

  const error = query?.error as { statusCode?: number } | null | undefined;
  const notFound = error?.statusCode === 404;

  function handleDelete() {
    confirm({
      title: 'Delete this question?',
      okText: 'Delete',
      danger: true,
      successMessage: 'Question deleted',
      onConfirm: async () => {
        await deleteQuestion({
          resource: 'questions',
          id: id!,
          meta: { companyId },
          // The confirm dialog above owns the success/error toast — suppress
          // Refine's own notification provider so the operator does not see
          // it twice (matches the list row's delete action).
          successNotification: false,
          errorNotification: false,
        });
        navigate('/questions');
      },
    });
  }

  const headerActions = (
    <Can I="manage" a="DiscoveryQuestion" passThrough>
      {(allowed: boolean) =>
        allowed ? (
          <Button danger onClick={handleDelete}>
            Delete
          </Button>
        ) : null
      }
    </Can>
  );

  // Do NOT hand DetailPage a disabled query — short-circuit entirely, same
  // prompt as list.tsx:173-182 / edit.tsx / create.tsx.
  if (companyId == null) {
    return (
      <div style={{ padding: space.xl }}>
        <Empty
          description={selectTenantPrompt('manage its Questions Inventory')}
          style={{ padding: '48px 0' }}
        />
      </div>
    );
  }

  const readView = question ? (
    <Space
      direction="vertical"
      size="large"
      style={{ width: '100%' }}
      data-testid="question-read-view"
    >
      <DetailField label="Question" value={question.text} />
      <DetailField label="Type" value={typeLabel(question)} />
      <DetailField
        label="Required"
        value={
          question.required ? <Tag color="gold">Required</Tag> : <Tag>Optional</Tag>
        }
      />
      <DetailField
        label="Default"
        value={question.isDefault ? <Tag>★ Default</Tag> : '—'}
      />
      {question.type === 'choice' && (
        <DetailField
          label='Includes "Other: ___"'
          value={question.hasOtherOption ? 'Yes' : 'No'}
        />
      )}
      <DetailField
        label="Answer options"
        value={
          question.type === 'free_text' ? (
            <Text type="secondary">
              No options. The rep types a free-form answer on the tear sheet — ideal
              for grabbing a cell phone, a referral name, or a one-off detail.
            </Text>
          ) : (
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              {[...question.options]
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((o) => (
                  <li key={o.id}>{o.text}</li>
                ))}
            </ol>
          )
        }
      />
      <DetailField label="Created" value={new Date(question.createdAt).toLocaleDateString()} />
      <DetailField label="Updated" value={new Date(question.updatedAt).toLocaleDateString()} />
    </Space>
  ) : null;

  return (
    <DetailPage
      title={question?.text ?? 'Question'}
      breadcrumbs={[
        { label: 'Questions Inventory', href: '/questions' },
        { label: question?.text ?? '' },
      ]}
      onNavigate={({ href }) => navigate(href)}
      onBack={() => navigate('/questions')}
      loading={query?.isLoading}
      error={notFound ? undefined : query?.error}
      notFound={notFound}
      notFoundHref="/questions"
      editing={editing}
      onEditingChange={(next) => {
        setEditing(next);
        // Leaving edit by ANY path (Cancel, back, a breadcrumb) must clear
        // `dirty` — not just the `onSaved` success path below. Otherwise a
        // dirty-then-discarded session leaves `dirty` stuck true, and the
        // NEXT edit session (freshly remounted, genuinely pristine) still
        // trips the discard guard on its first Cancel.
        if (!next) setDirty(false);
      }}
      editCap="manage:DiscoveryQuestion"
      dirty={dirty}
      actions={headerActions}
      readView={readView}
      editView={
        <QuestionEdit
          id={id}
          onDirtyChange={setDirty}
          onSaved={() => {
            setDirty(false);
            setEditing(false);
          }}
        />
      }
    />
  );
}
